import { FEATURE_FLAGS } from "./featureFlags";

/**
 * Flags the SERVER decides, fetched at runtime.
 *
 * `FEATURE_FLAGS` is compiled in: CRA replaces `process.env.REACT_APP_*` with
 * literals at build time, so inside a packaged APK those values are frozen
 * until a new build clears Play review. That is fine for a flag the client
 * owns, and wrong for `ENABLE_ROAMING`, which the client does not own — the
 * enforcing boundary is the server's discovery policy, and the documented
 * worst state is the two disagreeing (roaming UI in front of a server that
 * refuses it, i.e. every result 403s).
 *
 * So the build-time value is demoted to a DEFAULT, used until the server
 * answers, and the server's answer wins from then on.
 *
 * A tiny subscribe/notify store rather than React context: two of the four
 * consumers (`placeSearch`, `emptyMapReason`) are plain functions called
 * outside any component, and threading a provider through them would mean
 * changing signatures their tests are written against.
 */
const listeners = new Set();

// Mapbox's own floor, and what the map did before this was settable. Used
// until the server answers, and whenever it answers with nothing usable.
export const DEFAULT_MAP_MIN_ZOOM = 0;

// What every discovery call site used to hardcode. Kept as the fallback so an
// unreachable settings endpoint behaves exactly as the app did before.
export const DEFAULT_DISCOVERY_RADIUS_METERS = 50000;

let runtime = {
  // `null` means "the server has not told us yet" — distinct from `false`.
  roamingEnabled: null,
  mapMinZoom: null,
  discoveryRadiusMeters: null,
};

/** True when the server has answered at least once. */
export const hasServerFlags = () => runtime.roamingEnabled !== null;

/**
 * How far the map may zoom out, resolved.
 *
 * Falls back to Mapbox's own floor until the server answers, so the map is
 * never MORE constrained than it was before this setting existed.
 */
export const getMapMinZoom = () =>
  runtime.mapMinZoom === null ? DEFAULT_MAP_MIN_ZOOM : runtime.mapMinZoom;

/**
 * How far discovery should reach, resolved.
 *
 * The client is what ASKS for a radius and the server only clamps downward, so
 * a hardcoded request here silently capped the whole feature no matter what
 * the deployment allowed. Reading it from the server is what lets the reach be
 * changed without a Play release.
 */
export const getDiscoveryRadiusMeters = () =>
  runtime.discoveryRadiusMeters === null
    ? DEFAULT_DISCOVERY_RADIUS_METERS
    : runtime.discoveryRadiusMeters;

/**
 * Roaming, resolved.
 *
 * Read at CALL time, never captured at module load, so a flag that flips
 * mid-session takes effect without a reload.
 */
export const isRoamingEnabled = () =>
  runtime.roamingEnabled === null
    ? FEATURE_FLAGS.ENABLE_ROAMING
    : runtime.roamingEnabled;

/** Apply what the server said, and wake anything rendering off it. */
export const setRuntimeFlags = (next = {}) => {
  const roaming =
    typeof next.roamingEnabled === "boolean" ? next.roamingEnabled : null;
  // Bounded here as well as on the server: a value outside Mapbox's range
  // would throw inside the map rather than merely look wrong.
  const zoom =
    Number.isFinite(next.mapMinZoom) && next.mapMinZoom >= 0 && next.mapMinZoom <= 22
      ? next.mapMinZoom
      : null;

  // No-op if nothing actually changed: this is called on every app start and
  // a needless notify re-renders the map for nothing.
  const radius =
    Number.isFinite(next.discoveryRadiusMeters) && next.discoveryRadiusMeters >= 1
      ? Math.floor(next.discoveryRadiusMeters)
      : null;

  if (
    roaming === runtime.roamingEnabled &&
    zoom === runtime.mapMinZoom &&
    radius === runtime.discoveryRadiusMeters
  ) {
    return;
  }

  runtime = {
    ...runtime,
    roamingEnabled: roaming,
    mapMinZoom: zoom,
    discoveryRadiusMeters: radius,
  };
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      // One bad subscriber must not stop the others being told.
    }
  });
};

export const subscribeToRuntimeFlags = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/** Test seam — forget the server's answer and fall back to the build value. */
export const __resetRuntimeFlags = () => {
  runtime = { roamingEnabled: null, mapMinZoom: null, discoveryRadiusMeters: null };
  listeners.forEach((fn) => fn());
};
